import React, { useState } from 'react';
import { Box, Button, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { ADDITIONAL_FOLDER_TABS, MAX_ADDITIONAL_FOLDERS } from '@/utils/additionalFolderAutomation';
import { FolderRow } from './FolderRow';

export const AdditionalFolderFormContent = ({ canEdit, isLoading }) => {
	const {
		values,
		errors,
		touched,
		isSubmitting,
		getFieldProps,
		setFieldValue,
		setTouched,
		validateForm,
		submitForm,
	} = useFormikContext();
	const [activeTab, setActiveTab] = useState(ADDITIONAL_FOLDER_TABS[0].key);
	const tab =
		ADDITIONAL_FOLDER_TABS.find(({ key }) => key === activeTab) ?? ADDITIONAL_FOLDER_TABS[0];
	const additionalFolders = values[tab.key];
	const disabled = !canEdit || isSubmitting || isLoading;

	return (
		<Stack
			component={Form}
			onSubmit={async (event) => {
				event.preventDefault();
				const formErrors = await validateForm();
				setTouched(
					Object.fromEntries(
						ADDITIONAL_FOLDER_TABS.map(({ key }) => [key, values[key].map(() => true)]),
					),
				);

				const firstInvalidTab = ADDITIONAL_FOLDER_TABS.find(({ key }) =>
					formErrors[key]?.some(Boolean),
				);
				if (firstInvalidTab) {
					setActiveTab(firstInvalidTab.key);
					return;
				}

				submitForm();
			}}
			sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden' }}
		>
			<FolioDrawerHeader title='Additional folders' />

			<Typography variant='body2' color='text.secondary' sx={{ px: 2, pt: 1 }}>
				Choose which additional folders to create automatically when a new object is
				created.
			</Typography>

			<Tabs
				indicatorColor='primary'
				textColor='primary'
				variant='fullWidth'
				value={tab.key}
				onChange={(_event, next) => {
					setFieldValue(
						tab.key,
						additionalFolders.filter((name) => name.trim()),
					);
					setActiveTab(next);
				}}
				sx={{ px: 2, pt: 1, flexShrink: 0 }}
			>
				{ADDITIONAL_FOLDER_TABS.map(({ key, label }) => (
					<Tab key={key} value={key} label={label} sx={{ fontWeight: 'bold' }} />
				))}
			</Tabs>
			<Divider />

			<Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
				<Stack
					direction='row'
					alignItems='center'
					justifyContent='space-between'
					sx={{ mb: 1.5 }}
				>
					<Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
						Folders to create
					</Typography>
					<Button
						variant='text'
						size='small'
						disabled={disabled || additionalFolders.length >= MAX_ADDITIONAL_FOLDERS}
						onClick={() => {
							setFieldValue(tab.key, [...additionalFolders, '']);
						}}
						sx={{ fontWeight: 600 }}
					>
						Add folder
					</Button>
				</Stack>
				<Stack spacing={1}>
					{tab.defaultFolders.map((name) => (
						<FolderRow key={name} value={name} disabled />
					))}
					{additionalFolders.map((name, index) => (
						<FolderRow
							key={`${tab.key}-${index}`}
							{...getFieldProps(`${tab.key}.${index}`)}
							error={touched[tab.key]?.[index] && errors[tab.key]?.[index]}
							editable
							disabled={disabled}
							onDelete={() =>
								setFieldValue(
									tab.key,
									additionalFolders.filter((_, i) => i !== index),
								)
							}
						/>
					))}
				</Stack>
			</Box>

			<FolioDrawerFormGuard />
			<FolioDrawerFooter>
				<Button type='submit' variant='contained' disabled={disabled}>
					Save
				</Button>
			</FolioDrawerFooter>
		</Stack>
	);
};

export default AdditionalFolderFormContent;
